import geolib from "geolib";

/**
 * Check if 3 points are collinear (degenerate parallelogram).
 */
export const areCollinear = (p1, p2, p3, epsilon = 1e-10) => {
  const area = (p2[0] - p1[0]) * (p3[1] - p1[1]) - (p3[0] - p1[0]) * (p2[1] - p1[1]);
  return Math.abs(area) < epsilon;
};

/**
 * Compute the 4th corner of a parallelogram from 3 points.
 * p1, p2, p3 define corners; p4 = p1 + p3 - p2.
 */
export const computeParallelogramCorner = (p1, p2, p3) => [
  p1[0] + p3[0] - p2[0],
  p1[1] + p3[1] - p2[1],
];

/**
 * Subdivide a parallelogram (4 corners) into N equal strips.
 * Returns array of 4-vertex polygons in [lat, lng] format.
 */
export const subdivideParallelogram = (corners, count) => {
  const [p1, p2, p3, p4] = corners;
  const strips = [];
  for (let i = 0; i < count; i++) {
    const t0 = i / count;
    const t1 = (i + 1) / count;
    const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    strips.push([lerp(p1, p4, t0), lerp(p2, p3, t0), lerp(p2, p3, t1), lerp(p1, p4, t1)]);
  }
  return strips;
};

/**
 * Convert lat/lng polygon vertices to meter-based cartesian coordinates
 * relative to an anchor point.
 */
export const latLngToMeters = (vertices, anchor) =>
  vertices.map(([lat, lng]) => {
    const dist = geolib.getDistance(
      { latitude: anchor[0], longitude: anchor[1] },
      { latitude: lat, longitude: lng },
      1,
      13
    );
    const bearing = geolib.getBearing(
      { latitude: anchor[0], longitude: anchor[1] },
      { latitude: lat, longitude: lng }
    );
    const rad = (bearing * Math.PI) / 180;
    return { x: Math.sin(rad) * dist, y: Math.cos(rad) * dist };
  });

const OVERLAP_TOLERANCE_METERS = 0.02;

const projectPolygon = (vertices, axis) => {
  const len = Math.sqrt(axis[0] * axis[0] + axis[1] * axis[1]);
  const nx = axis[0] / len;
  const ny = axis[1] / len;
  let min = vertices[0].x * nx + vertices[0].y * ny;
  let max = min;
  for (let i = 1; i < vertices.length; i++) {
    const d = vertices[i].x * nx + vertices[i].y * ny;
    if (d < min) min = d;
    if (d > max) max = d;
  }
  return [min, max];
};

const getAxes = (vertices) =>
  vertices.map((v, i) => {
    const next = vertices[(i + 1) % vertices.length];
    return [-(next.y - v.y), next.x - v.x];
  });

/**
 * Check if two convex polygons overlap using Separating Axis Theorem
 * with 2cm tolerance, matching backend OverlapDetection.
 * Vertices are arrays of [lat, lng].
 */
export const polygonsOverlap = (vertsA, vertsB) => {
  const anchor = vertsA[0];
  const mA = latLngToMeters(vertsA, anchor);
  const mB = latLngToMeters(vertsB, anchor);
  const axes = [...getAxes(mA), ...getAxes(mB)];

  for (const axis of axes) {
    const [minA, maxA] = projectPolygon(mA, axis);
    const [minB, maxB] = projectPolygon(mB, axis);
    if (Math.min(maxA, maxB) - Math.max(minA, minB) <= OVERLAP_TOLERANCE_METERS) {
      return false;
    }
  }
  return true;
};
